// @ts-nocheck
import React from "react";
import { ReactComponent as ChevronRight } from "../assets/icons/chevron-right.svg";
import { useNavigate, useParams } from "react-router-dom";

// Retired. The breadcrumb bar is gone — a page now opens on its PageHeader, the way the admin app does, and the mobile menu trigger moved into the shell's top bar. Kept as a no-op so the ~39 pages that still render it don't need touching in one sweep; drop the call when a page gets its own PageHeader during the DS migration.
export const AssetBreadCrumb = (_props: {
  assetGroupName?: string;
  assetName?: string;
  organization?: any;
  children?: React.ReactNode;
  text?: string;
}) => null;

// Every routed page's frame, so the measure is decided in one place rather than 22. `eai-page-shell` in VerticalNav already centres at max-w-7xl, pads and owns the scroll, so a page is just a column on the app ground — no card, no second set of gutters. `fullHeight` is for pages that scroll a region of themselves instead of the shell (a chat pane, a map, a long table under a pinned header). They need a definite height to size that region against; everything else grows with its content.
export const PageContainer = ({
  children,
  fullHeight = false,
  className = "",
}: {
  children: React.ReactNode;
  fullHeight?: boolean;
  className?: string;
}) => (
  <div className={`${fullHeight ? "flex h-full min-h-0 w-full flex-col" : "w-full"} ${className}`}>
    {children}
  </div>
);

// Shared page header, on the admin app's shape: the website's // eyebrow, then an optional title, with an optional action pinned right and bottom-aligned to the title. With no title the eyebrow line carries the h1, so a page always has one.
export const PageHeader = ({
  eyebrow,
  title,
  description,
  descriptionClassName = "",
  action,
  className = "",
}: {
  eyebrow: React.ReactNode;
  title?: string;
  description?: React.ReactNode;
  descriptionClassName?: string;
  action?: React.ReactNode;
  className?: string;
}) => {
  const eyebrowLine = (
    <>
      {/* body-sm, not eai-slash's marketing 2xl — at title scale the slashes shout */}
      <span className="eai-slash mr-1.5 text-eai-body-sm leading-none">{"//"}</span>
      <span className="eai-eyebrow">{eyebrow}</span>
    </>
  );

  return (
    // eai-gap-header is the shared gap to the first block; a page's own mb-* utility still overrides it
    <div className={`eai-gap-header ${className}`}>
      {/* Action pinned right at every width; min-w-0 lets a long title wrap instead of pushing the action under it */}
      <div className="flex items-end justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          {title ? (
            <div className="mb-1.5 flex items-center">{eyebrowLine}</div>
          ) : (
            <h1 className="mb-1.5 flex items-center">{eyebrowLine}</h1>
          )}
          {title && <h1 className="eai-page-title">{title}</h1>}
        </div>
        {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
      </div>
      {description && (
        <span className={`eai-description-sm mt-1 block ${descriptionClassName}`}>
          {description}
        </span>
      )}
    </div>
  );
};

// One empty state for every list page — eai-panel because it's a page block, not chrome, and the glyph takes a rail card's no-picture badge
export const PageEmptyState = ({
  icon,
  title,
  description,
  action,
  className = "",
}: {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`eai-panel flex flex-col items-center justify-center gap-4 px-6 py-14 text-center ${className}`}
  >
    {icon && (
      <div className="flex h-14 w-14 items-center justify-center rounded-full border border-eai-border bg-eai-surface text-eai-ink-ghost">
        {icon}
      </div>
    )}
    {title && <p className="eai-h3-title">{title}</p>}
    {description && <p className="eai-description-sm max-w-md">{description}</p>}
    {action && <div className="flex flex-wrap items-center justify-center gap-2">{action}</div>}
  </div>
);

export const NestedBreadCrumbs = ({ items, children }) => {
  const navigate = useNavigate();
  const { groupid } = useParams();

  // Craig edit this so last item does not cause navigation
  return (
    <>
      <div className="bg-white h-12 rounded-t-md border border-eai-border border-b-0">
        <div className="flex grow items-center p-2">
          {children}
          {items.map((item, i) => {
            return (
              <React.Fragment key={item.route}>
                <div
                  onClick={() => navigate(item.route)}
                  className="cursor-pointer font-inter text-eai-control text-eai-ink-soft mr-1 ml-3"
                >
                  {item.label}
                </div>
                {items.length !== i + 1 && <ChevronRight className="mr-1 text-eai-primary-500 [&_path]:stroke-current" />}
              </React.Fragment>
            );
          })}
          <div className=" h-8 "></div>
        </div>
      </div>
    </>
  );
};
